import React, { useEffect, useMemo, useState } from 'react';
import './HelpGuideModal.css';

type IconProps = {
  size?: number;
  className?: string;
};

type GuideSection = {
  id: string;
  title: string;
  shortTitle: string;
  icon: React.ComponentType<IconProps>;
  summary: string;
  bullets: string[];
  quickSteps: string[];
};

const HelpCircle = ({ size = 18, className = '' }: IconProps) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <path d="M9.09 9a3 3 0 1 1 5.82 1c0 2-3 3-3 3" />
    <path d="M12 17h.01" />
  </svg>
);

const BookOpen = ({ size = 18, className = '' }: IconProps) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M2 19.5A2.5 2.5 0 0 1 4.5 17H20" />
    <path d="M4.5 2H20v20H4.5A2.5 2.5 0 0 1 2 19.5v-15A2.5 2.5 0 0 1 4.5 2z" />
  </svg>
);

const Brain = ({ size = 18, className = '' }: IconProps) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9.5 2a2.5 2.5 0 0 0-2.47 2.11A3.5 3.5 0 0 0 5 7.5v1A3.5 3.5 0 0 0 6 15.35V17a3 3 0 0 0 3 3" />
    <path d="M14.5 2a2.5 2.5 0 0 1 2.47 2.11A3.5 3.5 0 0 1 19 7.5v1A3.5 3.5 0 0 1 18 15.35V17a3 3 0 0 1-3 3" />
    <path d="M9 12h6" />
    <path d="M12 2v20" />
  </svg>
);

const Sparkles = ({ size = 18, className = '' }: IconProps) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="m12 3-1.9 5.1L5 10l5.1 1.9L12 17l1.9-5.1L19 10l-5.1-1.9L12 3z" />
    <path d="M5 3v4" />
    <path d="M3 5h4" />
    <path d="M19 17v4" />
    <path d="M17 19h4" />
  </svg>
);

const CheckCircle2 = ({ size = 18, className = '' }: IconProps) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="12" r="10" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

const Printer = ({ size = 18, className = '' }: IconProps) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 9V2h12v7" />
    <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
    <path d="M6 14h12v8H6z" />
  </svg>
);

const X = ({ size = 18, className = '' }: IconProps) => (
  <svg className={className} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
);

const sections: GuideSection[] = [
  {
    id: 'recording',
    title: 'Mark Recording & Weighting',
    shortTitle: 'Marks',
    icon: BookOpen,
    summary: 'Capture marks assessment by assessment, then let MarkSure handle weighting and conversions using the structure already stored for the subject.',
    bullets: [
      'Open the subject, select the assessment section, and capture raw learner marks exactly as written on the script.',
      'For Foundation Phase and CAPS-based groupings, MarkSure uses the saved assessment groups to calculate converted totals automatically.',
      'Use the Export Marks screen to confirm how raw totals, scaled totals, and report-ready values line up before moderation.'
    ],
    quickSteps: [
      'Choose the correct term and subject.',
      'Enter raw marks for each assessment section.',
      'Check the converted total or weighting preview.',
      'Export when the class set is complete.'
    ]
  },
  {
    id: 'diagnostics',
    title: 'AI Diagnostics & At-Risk',
    shortTitle: 'Diagnostics',
    icon: Brain,
    summary: 'Diagnostics reads recorded assessment data and highlights weak areas, at-risk learners, and where understanding drops between basic recall and higher-order application.',
    bullets: [
      'Select the subject, term, and assessment so the AI uses real captured section marks from MarkSure.',
      'Weak areas point to topics where class averages are low, while at-risk flags identify learners whose overall performance suggests targeted intervention is needed.',
      'Use the cognitive split idea practically: if Knowledge items are fine but Problem Solving is weak, reteach strategy, reasoning, and worked examples instead of only definitions.'
    ],
    quickSteps: [
      'Pick a subject and assessment with recorded marks.',
      'Add teacher context like language barriers or pacing issues.',
      'Run diagnostics and read the weak-area summary first.',
      'Use the suggested interventions to plan reteaching.'
    ]
  },
  {
    id: 'rubrics',
    title: 'Rubric Builder & Marking',
    shortTitle: 'Rubrics',
    icon: Sparkles,
    summary: 'Generate a printable rubric in seconds, then edit the criteria and descriptors before using it for classroom marking.',
    bullets: [
      'Start with grade, subject, task type, and total marks so the rubric aligns to the real assessment you already created.',
      'Review each criterion carefully and adjust wording, marks, or performance levels before applying it.',
      'Use the rubric to standardize marking across classes and keep moderation cleaner and faster.'
    ],
    quickSteps: [
      'Open AI Tools and generate a rubric from the assessment context.',
      'Edit criteria, levels, and notes as needed.',
      'Save or export the finished rubric for use in class.',
      'Reuse the structure for similar tasks later.'
    ]
  },
  {
    id: 'planner',
    title: 'Weekly Lesson Planner',
    shortTitle: 'Planner',
    icon: CheckCircle2,
    summary: 'Build CAPS-aligned weekly lesson plan grids, refine every lesson cell, and print a clean PDF for your file or HOD checks.',
    bullets: [
      'Open Lesson Plans, fill in school, subject, grade, term, week, date range, classes, and the topic focus for the week.',
      'The generated table includes lesson titles, core knowledge, page references, homework, and assessments for the week.',
      'Edit any cell before printing so the final plan reflects your textbook, pace, and class context.'
    ],
    quickSteps: [
      'Open the Lesson Plans module.',
      'Complete the generator form with weekly context.',
      'Generate, then edit the lesson grid cell by cell.',
      'Use Print / Save PDF for the final copy.'
    ]
  },
  {
    id: 'exporting',
    title: 'Exporting & Reporting',
    shortTitle: 'Reporting',
    icon: Printer,
    summary: 'When moderation or reporting is due, MarkSure can export class statistics, raw mark sheets, and printable documents for review.',
    bullets: [
      'Use Export Marks to choose the term, classes, and the assessments you want included in the workbook.',
      'Keep the screen focused by selecting only the classes you need, then expand export adjustments only when you want converted totals.',
      'Share exported sheets with HODs for moderation, moderation evidence, and reporting checks.'
    ],
    quickSteps: [
      'Go to Export Marks and pick the term.',
      'Select the classes and assessments to include.',
      'Choose whether raw mark sheets must be added.',
      'Export the workbook or print the relevant pages.'
    ]
  }
];

export default function HelpGuideModal(): JSX.Element {
  const [isOpen, setIsOpen] = useState(false);
  const [activeSectionId, setActiveSectionId] = useState(sections[0].id);
  const [tourIndex, setTourIndex] = useState(0);
  const [mode, setMode] = useState<'tabs' | 'tour'>('tabs');

  const activeSection = useMemo(
    () => sections.find((section) => section.id === activeSectionId) || sections[0],
    [activeSectionId]
  );

  const tourSection = sections[tourIndex] || sections[0];

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const tagName = target?.tagName || '';
      const isEditable = Boolean(
        target?.isContentEditable ||
        tagName === 'INPUT' ||
        tagName === 'TEXTAREA' ||
        tagName === 'SELECT'
      );

      if (event.key === 'Escape') {
        setIsOpen(false);
        return;
      }

      const wantsShortcut = event.key === '?' || ((event.ctrlKey || event.metaKey) && event.key === '/');
      if (wantsShortcut && !isEditable) {
        event.preventDefault();
        setIsOpen(true);
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  const openGuide = () => setIsOpen(true);
  const closeGuide = () => setIsOpen(false);

  const handleTabSelect = (sectionId: string) => {
    setMode('tabs');
    setActiveSectionId(sectionId);
  };

  const handleTourStart = () => {
    setMode('tour');
    setTourIndex(0);
    setActiveSectionId(sections[0].id);
  };

  const handleTourNext = () => {
    setTourIndex((current) => Math.min(current + 1, sections.length - 1));
  };

  const handleTourPrevious = () => {
    setTourIndex((current) => Math.max(current - 1, 0));
  };

  return (
    <>
      <button
        type="button"
        className="help-guide-trigger"
        onClick={openGuide}
        aria-haspopup="dialog"
        aria-expanded={isOpen}
        aria-controls="marksure-help-guide"
      >
        <span className="help-guide-trigger__icon"><HelpCircle size={18} /></span>
        <span className="help-guide-trigger__label">Help &amp; Quick Guide</span>
        <span className="help-guide-trigger__shortcut">?</span>
      </button>

      {isOpen && (
        <div className="help-guide-overlay" onClick={closeGuide} role="presentation">
          <div
            id="marksure-help-guide"
            className="help-guide-drawer"
            role="dialog"
            aria-modal="true"
            aria-labelledby="marksure-help-guide-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="help-guide-header">
              <div>
                <p className="help-guide-kicker">Quick Onboarding</p>
                <h2 id="marksure-help-guide-title">MarkSure Help Guide</h2>
                <p className="help-guide-subtitle">
                  Use the tabs for reference or switch to tour mode for a guided walkthrough of the main workflows.
                </p>
              </div>
              <button type="button" className="help-guide-close" onClick={closeGuide} aria-label="Close help guide">
                <X size={18} />
              </button>
            </div>

            <div className="help-guide-toolbar">
              <div className="help-guide-mode-switch">
                <button
                  type="button"
                  className={`help-guide-mode-btn ${mode === 'tabs' ? 'active' : ''}`}
                  onClick={() => setMode('tabs')}
                >
                  Reference Tabs
                </button>
                <button
                  type="button"
                  className={`help-guide-mode-btn ${mode === 'tour' ? 'active' : ''}`}
                  onClick={handleTourStart}
                >
                  Quick Tour
                </button>
              </div>
              <div className="help-guide-shortcut-hint">Shortcut: <kbd>?</kbd> or <kbd>Ctrl/Cmd + /</kbd></div>
            </div>

            {mode === 'tabs' ? (
              <div className="help-guide-content">
                <div className="help-guide-tabs" role="tablist" aria-label="Help guide sections">
                  {sections.map((section) => {
                    const Icon = section.icon;
                    const isActive = section.id === activeSection.id;

                    return (
                      <button
                        key={section.id}
                        type="button"
                        role="tab"
                        aria-selected={isActive}
                        className={`help-guide-tab ${isActive ? 'active' : ''}`}
                        onClick={() => handleTabSelect(section.id)}
                      >
                        <Icon size={18} />
                        <span>{section.title}</span>
                      </button>
                    );
                  })}
                </div>

                <div className="help-guide-panel" role="tabpanel">
                  <div className="help-guide-panel-hero">
                    <div className="help-guide-panel-badge">
                      <activeSection.icon size={20} />
                      <span>{activeSection.shortTitle}</span>
                    </div>
                    <h3>{activeSection.title}</h3>
                    <p>{activeSection.summary}</p>
                  </div>

                  <div className="help-guide-detail-grid">
                    <section className="help-guide-card">
                      <h4>What To Do</h4>
                      <ul>
                        {activeSection.bullets.map((item) => (
                          <li key={item}>{item}</li>
                        ))}
                      </ul>
                    </section>
                    <section className="help-guide-card">
                      <h4>Quick Workflow</h4>
                      <ol>
                        {activeSection.quickSteps.map((step) => (
                          <li key={step}>{step}</li>
                        ))}
                      </ol>
                    </section>
                  </div>
                </div>
              </div>
            ) : (
              <div className="help-guide-tour">
                <div className="help-guide-tour-progress">
                  <span>Step {tourIndex + 1} of {sections.length}</span>
                  <div className="help-guide-tour-track" aria-hidden="true">
                    <div
                      className="help-guide-tour-fill"
                      style={{ width: `${((tourIndex + 1) / sections.length) * 100}%` }}
                    />
                  </div>
                </div>

                <div className="help-guide-panel">
                  <div className="help-guide-panel-hero">
                    <div className="help-guide-panel-badge">
                      <tourSection.icon size={20} />
                      <span>{tourSection.shortTitle}</span>
                    </div>
                    <h3>{tourSection.title}</h3>
                    <p>{tourSection.summary}</p>
                  </div>

                  <section className="help-guide-card">
                    <h4>Walkthrough</h4>
                    <ol>
                      {tourSection.quickSteps.map((step) => (
                        <li key={step}>{step}</li>
                      ))}
                    </ol>
                  </section>
                </div>

                <div className="help-guide-tour-actions">
                  <button type="button" className="help-guide-mode-btn" onClick={handleTourPrevious} disabled={tourIndex === 0}>
                    Previous
                  </button>
                  <button
                    type="button"
                    className="help-guide-mode-btn active"
                    onClick={tourIndex === sections.length - 1 ? () => setMode('tabs') : handleTourNext}
                  >
                    {tourIndex === sections.length - 1 ? 'Finish Tour' : 'Next Step'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
